<template>
  <ChargingStationsTable
    v-bind="$attrs"
    :total-matching="totalMatching"
    :enable-select-all-matching="effectiveEnableSelectAllMatching"
    :selection-state="bannerState"
    :matching-selected="matchingSelected"
    :excluded-row-keys="selection.excludedKeys.value"
    :selection-message="selectionMessage"
    @selected="onSelected"
    @search="onSearch"
    @select-all-matching="onSelectAllMatching"
    @toggle-matching-row="onToggleMatchingRow"
    @select-matching-on-screen="onSelectMatchingOnScreen"
  >
    <!-- Explicit, not a v-for over $slots: Vue drops dynamic slots next to a static one. -->
    <template v-if="$slots.actions" #actions>
      <slot name="actions" />
    </template>

    <template #selectionActions>
      <div v-if="showBulkActions && count > 0" class="flex items-center justify-center gap-4 py-1">
        <InfuseTooltip v-if="singleBulkAction" :content="disabledReason(singleBulkAction)">
          <InfuseButton
            color="primary"
            size="small"
            class="px-3"
            :loading="resolvingAction"
            :disabled="isActionDisabled(singleBulkAction)"
            @click="onActionClick(singleBulkAction)"
          >
            <div class="flex items-center justify-center gap-1.5">
              <font-awesome-icon :icon="singleBulkAction.icon" />
              <span>{{ singleBulkAction.label() }}</span>
            </div>
          </InfuseButton>
        </InfuseTooltip>
        <div v-else ref="bulkMenuRef" class="relative" @keydown.esc="bulkMenuOpen = false">
          <InfuseButton
            color="primary"
            size="small"
            class="px-3"
            :loading="resolvingAction"
            @click="bulkMenuOpen = !bulkMenuOpen"
          >
            <div class="flex items-center justify-center gap-1.5">
              <span>{{ t('chargingStation.overview.bulkAction') }}</span>
              <font-awesome-icon :icon="['far', bulkMenuOpen ? 'chevron-up' : 'chevron-down']" class="text-xs" />
            </div>
          </InfuseButton>

          <div
            v-if="bulkMenuOpen"
            class="absolute right-0 bottom-full z-30 mb-2 min-w-48 rounded-lg bg-white py-2 text-sm shadow-md shadow-gray-300"
          >
            <InfuseTooltip
              v-for="action in availableBulkActions"
              :key="action.key"
              :content="disabledReason(action)"
            >
              <button
                type="button"
                v-permission="{ id: action.permissionId, hide: true }"
                :disabled="isActionDisabled(action)"
                class="flex w-full items-center px-4 py-2 text-left font-semibold"
                :class="isActionDisabled(action)
                  ? 'cursor-not-allowed text-neutral-400 opacity-50'
                  : 'cursor-pointer text-neutral-700 hover:text-primary-500'"
                @click="onActionClick(action)"
              >
                <font-awesome-icon :icon="action.icon" class="mr-2" />
                {{ action.label() }}
              </button>
            </InfuseTooltip>
          </div>
        </div>
      </div>
    </template>
  </ChargingStationsTable>

  <InfuseConfirmModal
    v-if="showConfirm"
    :action-title="t('chargingStation.overview.confirmBulk.title', { count })"
    @close="cancelConfirm"
    @confirm="confirmAction"
  >
    {{ t('chargingStation.overview.confirmBulk.message', { count }) }}
  </InfuseConfirmModal>
</template>

<script lang="ts" setup>
import { computed, ref, useAttrs, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useNotify } from '@infuse/cpms/ui';
import { batchPermissions } from '@infuse/api/cpms/batch';
import { BatchJobType, type StationSelector, toStationSelector } from '@infuse/api/cpms/batch';
import type { ChargingStation, StationSelection } from '@infuse/cpms/charging-station/store';
import {
  groupStations,
  type StationGroup,
  useChargingStationStore,
  useStationSelection
} from '@infuse/cpms/charging-station/store';
import { Feature, useFeatureStore } from '@infuse/cpms/shared/features/store';
import { useCurrentUserStore } from '@infuse/iam/user/store';
import { vPermission } from '@infuse/iam/permission/directives';
import { InfuseButton, InfuseConfirmModal, InfuseTooltip } from '@infuse/ui/components';
import { bulkActionStateFor, useAnalyticsTracker, useBulkActionLimits } from '@infuse/utils';
import ChargingStationsTable from './ChargingStationsTable.vue';

defineOptions({ inheritAttrs: false });

interface Props {
  totalMatching?: number;
  currentFilter?: string;
  /** Defaults to whether the Bulk Action menu is shown. */
  enableSelectAllMatching?: boolean;
  context?: 'within-action' | 'start-action';
  /** Off by default: a page that hasn't wired `@bulk-update` would show a dead button. */
  enableBulkActions?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  totalMatching: undefined,
  currentFilter: undefined,
  // Explicit, or Vue casts an absent boolean prop to false and the fallback never applies.
  enableSelectAllMatching: undefined,
  context: 'start-action',
  enableBulkActions: false
});

const emit = defineEmits<{
  'update:selection': [selection: StationSelection<ChargingStation>];
  'bulk-update': [payload: BulkUpdatePayload];
}>();

const { t } = useI18n();
const { notifyFailure } = useNotify();
const featureStore = useFeatureStore();
const currentUserStore = useCurrentUserStore();
const store = useChargingStationStore();
const analytics = useAnalyticsTracker();
const limits = useBulkActionLimits();
const selection = useStationSelection<ChargingStation>();

const count = computed(() => selection.count.value);
const matchingSelected = computed(() => selection.mode.value === 'filter');

const effectiveEnableSelectAllMatching = computed(
  () => (props.enableSelectAllMatching ?? showBulkActions.value) && props.context !== 'within-action'
);

// No banner for a limit that no available action is subject to.
const limitsApply = computed(() =>
  availableBulkActions.value.some((action) => !action.blockExempt || !action.confirmExempt)
);

const bannerState = computed(() => {
  if (!showBulkActions.value || !limitsApply.value) return 'normal';
  const state = bulkActionStateFor(count.value, limits.value, false);
  return state === 'disabled' ? 'normal' : state;
});

const selectionMessage = computed(() =>
  bannerState.value === 'block'
    ? t('chargingStation.overview.tooManySelected', {
        count: count.value,
        blockAt: limits.value?.blockAt
      })
    : undefined
);

const hasFirmwareBatch = computed(() => featureStore.hasFeature(Feature.BATCH_RUNNER_FIRMWARE));
const canCreateBatch = computed(
  () => !currentUserStore.enabled || currentUserStore.hasPermission(batchPermissions.CREATE)
);

const availableBulkActions = computed(() => bulkActions.value.filter((action) => action.isAvailable()));
const showBulkActions = computed(
  () => props.enableBulkActions && props.context === 'start-action' && availableBulkActions.value.length > 0
);
const singleBulkAction = computed(() =>
  availableBulkActions.value.length === 1 ? availableBulkActions.value[0] : undefined
);

const bulkMenuRef = ref<HTMLElement | null>(null);
const bulkMenuOpen = ref(false);
onClickOutside(bulkMenuRef, () => (bulkMenuOpen.value = false));
const showConfirm = ref(false);
const pendingAction = ref<BulkAction | null>(null);
const resolvingAction = ref(false);

async function resolveSelection(): Promise<{ selector: StationSelector; groups: StationGroup[] }> {
  const sel = selection.selection.value;
  if (sel.mode === 'ids') {
    return {
      selector: toStationSelector({ mode: 'ids', rows: sel.rows }),
      groups: groupStations(sel.rows)
    };
  }
  // The batch runner only takes ids, and filter-mode rows were never loaded.
  const { ids, groups } = await store.fetchAllMatchingIds(new Set(sel.excludedIds));
  return { selector: { type: 'ID', chargingStationIds: ids }, groups };
}

export interface BulkUpdatePayload {
  selector: StationSelector;
  count: number;
  groups: StationGroup[];
  batchType: BatchJobType;
}

interface BulkAction {
  key: string;
  label: () => string;
  icon: string[];
  permissionId: string;
  isAvailable: () => boolean;
  blockExempt: boolean;
  confirmExempt: boolean;
  disabled?: () => boolean;
  disabledReason?: () => string;
  run: () => void;
}

const bulkActions = computed<BulkAction[]>(() => [
  {
    key: 'firmware',
    label: () => t('chargingStation.overview.firmwareUpdate'),
    icon: ['fas', 'upload'],
    permissionId: batchPermissions.CREATE,
    isAvailable: () => hasFirmwareBatch.value && canCreateBatch.value,
    blockExempt: true,
    confirmExempt: true,
    run: async () => {
      resolvingAction.value = true;
      // Resolving can page the API; a search meanwhile would change what the ids describe.
      const actedCount = count.value;
      const actedMode = selection.mode.value;
      try {
        const { selector, groups } = await resolveSelection();
        if (selection.mode.value !== actedMode || count.value !== actedCount) {
          notifyFailure({
            title: t('chargingStation.overview.bulkActionFailed.title'),
            message: t('chargingStation.overview.bulkActionFailed.message')
          });
          return;
        }
        analytics.track('Bulk Action Executed', {
          action: 'firmware',
          count: actedCount,
          mode: actedMode,
          typeCount: groups.length
        });
        emit('bulk-update', { selector, count: actedCount, groups, batchType: BatchJobType.FIRMWARE });
      } catch {
        notifyFailure({
          title: t('chargingStation.overview.bulkActionFailed.title'),
          message: t('chargingStation.overview.bulkActionFailed.message')
        });
      } finally {
        resolvingAction.value = false;
      }
    }
  }
]);

const rawSelectionState = computed(() => bulkActionStateFor(count.value, limits.value, false));

function isActionDisabled(action: BulkAction): boolean {
  return action.disabled?.() === true || (!action.blockExempt && rawSelectionState.value === 'block');
}

function actionNeedsConfirm(action: BulkAction): boolean {
  return !action.confirmExempt && rawSelectionState.value !== 'normal';
}

function disabledReason(action: BulkAction): string {
  if (!isActionDisabled(action)) return '';
  if (action.disabled?.()) return action.disabledReason?.() ?? '';
  return t('chargingStation.overview.actionOverLimit');
}

function onActionClick(action: BulkAction) {
  if (isActionDisabled(action)) return;
  bulkMenuOpen.value = false;
  if (actionNeedsConfirm(action)) {
    pendingAction.value = action;
    showConfirm.value = true;
  } else {
    action.run();
  }
}

function confirmAction() {
  showConfirm.value = false;
  pendingAction.value?.run();
  pendingAction.value = null;
}

function cancelConfirm() {
  showConfirm.value = false;
  pendingAction.value = null;
}

function emitSelection() {
  emit('update:selection', selection.selection.value);
}

function onSelected(stations: ChargingStation[]) {
  selection.setSelectedRows(stations);
  emitSelection();
}

function onToggleMatchingRow(row: ChargingStation) {
  selection.toggle(row);
  emitSelection();
}

function onSelectMatchingOnScreen(visibleRows: ChargingStation[]) {
  selection.selectAllOnScreen(visibleRows);
  emitSelection();
}

function onSelectAllMatching() {
  selection.selectAllMatching(props.currentFilter ?? '', props.totalMatching ?? 0);
  analytics.track('Bulk Action Select All Matching', { count: props.totalMatching ?? 0 });
  emitSelection();
}

function onSearch() {
  selection.notifyFilterChanged();
  emitSelection();
}

watch(bannerState, (state, previous) => {
  if (state === 'warn' && previous !== 'warn') {
    analytics.track('Bulk Action Warning Shown', { count: count.value });
  } else if (state === 'block' && previous !== 'block') {
    analytics.track('Bulk Action Limit Exceeded Shown', { count: count.value });
  }
});

const attrs = useAttrs();
watch(
  () => attrs.filters,
  () => {
    selection.notifyFilterChanged();
    emitSelection();
  },
  { deep: true }
);
</script>
